import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { StockRow } from '../stock/public-api';
import { countStockByBranch, getStockByBranch } from '../stock/public-api';
import { CATALOG_ADMIN_PAGE_SIZE_STORAGE_KEY } from '../../catalog/domain-api'
import { preflightCatalogSnapshotForRead } from '../../catalog/infra-api'
import { searchProducts } from '../../catalog/infra-api';
import { effectiveProductSearchQuery, PRODUCT_SEARCH_DEBOUNCE_MS } from '../../shared/product-search/productSearchUx';
import type { StockSort, StockStatusFilter } from '../stock/public-api';
import { matchesSearchText } from '../../shared/format/searchText';
import type { ProductSearchRow as InventoryProductSearchRow } from '../../catalog/domain-api';
import { usePaginatedRemoteList } from '../../shared/data/usePaginatedRemoteList';
import { isDatabaseUnavailableError } from '../../shared/localDb';
import { useDebouncedValue } from '../../shared/ui/hooks-ui/useDebouncedValue';
import { subscribeCatalogSynced } from '../../sync/core/catalogSyncListeners';
import { useInventoryUiStore } from '../store/useInventoryUiStore';

type Params = {
  currentBranchId: string | null | undefined;
  /** When statusFilter is no_sales, restrict list to these product IDs from RPC. */
  noSalesProductIds?: string[] | null;
};

type StockListQuery = {
  branchId: string;
  search?: string;
  pinnedProductId?: string;
  statusFilter: StockStatusFilter;
  categoryId?: string;
  brandId?: string;
  supplierId?: string;
  productIdAllowlist?: string[];
};

export const useInventoryStockListFlow = ({ currentBranchId, noSalesProductIds }: Params) => {
  const lastStocksPreflightBranchKeyRef = useRef<string | null>(null);

  const [searchQuery, setSearchQuery] = useState('');
  const stockSearchDebounced = useDebouncedValue(searchQuery.trim(), PRODUCT_SEARCH_DEBOUNCE_MS);
  const [stockFilter, setStockFilter] = useState<StockStatusFilter>('all');
  const [stockCategoryFilter, setStockCategoryFilter] = useState<string | 'all'>('all');
  const [stockBrandFilter, setStockBrandFilter] = useState<string | null>(null);
  const [stockSupplierFilter, setStockSupplierFilter] = useState<string | null>(null);
  const [pinnedStockProduct, setPinnedStockProduct] = useState<{ id: string; label: string } | null>(null);
  const [stockSort, setStockSort] = useState<StockSort>('name_asc');

  const clearPinnedStockSearch = useCallback(() => setPinnedStockProduct(null), []);

  const applyStockSort = useCallback(
    (next: StockSort) => {
      setStockSort(next);
    },
    [],
  );

  const applyStockQuickViewFilter = useCallback(
    (next: StockStatusFilter) => {
      clearPinnedStockSearch();
      setStockFilter(next);
    },
    [clearPinnedStockSearch],
  );

  const applyStockCategoryFilter = useCallback(
    (next: string | 'all') => {
      clearPinnedStockSearch();
      setStockCategoryFilter(next);
    },
    [clearPinnedStockSearch],
  );

  const applyStockBrandFilter = useCallback(
    (next: string | null) => {
      clearPinnedStockSearch();
      setStockBrandFilter(next);
    },
    [clearPinnedStockSearch],
  );

  const applyStockSupplierFilter = useCallback(
    (next: string | null) => {
      clearPinnedStockSearch();
      setStockSupplierFilter(next);
    },
    [clearPinnedStockSearch],
  );

  const onInventorySearchFieldChange = useCallback(
    (text: string) => {
      if (pinnedStockProduct != null) clearPinnedStockSearch();
      setSearchQuery(text);
    },
    [pinnedStockProduct, clearPinnedStockSearch],
  );

  const stockListSearchQuery = useMemo(
    () => effectiveProductSearchQuery(stockSearchDebounced),
    [stockSearchDebounced],
  );

  const stockListCriteriaKey = useMemo(
    () =>
      `${currentBranchId ?? ''}|${stockFilter}|${stockListSearchQuery}|${stockCategoryFilter}|${pinnedStockProduct?.id ?? ''}|${stockBrandFilter ?? ''}|${stockSupplierFilter ?? ''}|${stockSort}|${stockFilter === 'no_sales' ? (noSalesProductIds ?? []).join(',') : ''}`,
    [
      currentBranchId,
      stockFilter,
      stockListSearchQuery,
      stockCategoryFilter,
      pinnedStockProduct?.id,
      stockBrandFilter,
      stockSupplierFilter,
      stockSort,
      noSalesProductIds,
    ],
  );

  const listQuery = useMemo<StockListQuery | null>(() => {
    if (!currentBranchId) return null;
    return {
      branchId: currentBranchId,
      search: pinnedStockProduct ? undefined : stockListSearchQuery || undefined,
      pinnedProductId: pinnedStockProduct?.id,
      statusFilter: stockFilter,
      categoryId: stockCategoryFilter !== 'all' ? stockCategoryFilter : undefined,
      brandId: stockBrandFilter ?? undefined,
      supplierId: stockSupplierFilter ?? undefined,
      productIdAllowlist: stockFilter === 'no_sales' ? [...(noSalesProductIds ?? [])] : undefined,
    };
  }, [
    currentBranchId,
    pinnedStockProduct,
    stockListSearchQuery,
    stockFilter,
    stockCategoryFilter,
    stockBrandFilter,
    stockSupplierFilter,
    noSalesProductIds,
  ]);

  const fetchStockPage = useCallback(
    async ({ limit, offset, query }: { limit: number; offset: number; query: StockListQuery }) => {
      if (lastStocksPreflightBranchKeyRef.current !== query.branchId) {
        await preflightCatalogSnapshotForRead();
        lastStocksPreflightBranchKeyRef.current = query.branchId;
      }
      return getStockByBranch(query.branchId, {
        limit,
        offset,
        search: query.search,
        pinnedProductId: query.pinnedProductId,
        statusFilter: query.statusFilter,
        categoryId: query.categoryId,
        brandId: query.brandId,
        supplierId: query.supplierId,
        productIdAllowlist: query.productIdAllowlist,
        sort: stockSort,
      });
    },
    [stockSort],
  );

  const countStock = useCallback(
    (query: StockListQuery) =>
      countStockByBranch(query.branchId, {
        search: query.search,
        pinnedProductId: query.pinnedProductId,
        statusFilter: query.statusFilter,
        categoryId: query.categoryId,
        brandId: query.brandId,
        supplierId: query.supplierId,
        productIdAllowlist: query.productIdAllowlist,
      }),
    [],
  );

  const paging = usePaginatedRemoteList<StockRow, StockListQuery>({
    storageKey: CATALOG_ADMIN_PAGE_SIZE_STORAGE_KEY,
    defaultPageSize: 25,
    criteriaKey: stockListCriteriaKey,
    query: listQuery ?? { branchId: '', statusFilter: 'all' },
    enabled: listQuery != null,
    logLabel: 'Inventory',
    fetchPage: fetchStockPage,
    count: countStock,
  });

  // #801: Expand/collapse variant rows.
  const expandedStockProductIds = useInventoryUiStore((s) => s.expandedStockProductIds);
  const toggleStockProductExpanded = useInventoryUiStore((s) => s.toggleStockProductExpanded);
  const [variantRowsByProduct, setVariantRowsByProduct] = useState<Map<string, StockRow[]>>(new Map());
  // Error al expandir variantes por producto — la UI lo muestra inline con retry.
  const [variantRowsErrorByProduct, setVariantRowsErrorByProduct] = useState<Record<string, string>>({});

  // Load variant rows for a product when it's expanded.
  const loadVariantRows = useCallback(
    async (productId: string, branchId: string) => {
      try {
        const allRows = await getStockByBranch(branchId, {
          pinnedProductId: productId,
          includeVariants: true,
          sort: stockSort,
        });
        const variants = allRows.filter((r) => r.variantId != null && r.variantId !== '');
        setVariantRowsByProduct((prev) => {
          const next = new Map(prev);
          next.set(productId, variants);
          return next;
        });
        setVariantRowsErrorByProduct((prev) => {
          if (!(productId in prev)) return prev;
          const next = { ...prev };
          delete next[productId];
          return next;
        });
      } catch (error) {
        if (!isDatabaseUnavailableError(error)) {
          console.warn('[Inventory] No se pudieron cargar las variantes', error);
        }
        setVariantRowsErrorByProduct((prev) => ({
          ...prev,
          [productId]: error instanceof Error ? error.message : 'error',
        }));
      }
    },
    [stockSort],
  );

  const retryVariantRows = useCallback(
    (productId: string) => {
      if (!currentBranchId) return;
      void loadVariantRows(productId, currentBranchId);
    },
    [currentBranchId, loadVariantRows],
  );

  // Reload variant rows when catalog syncs or when expanded set changes.
  useEffect(() => {
    if (!currentBranchId || expandedStockProductIds.size === 0) return;
    for (const productId of expandedStockProductIds) {
      void loadVariantRows(productId, currentBranchId);
    }
  }, [expandedStockProductIds, currentBranchId, loadVariantRows]);

  useEffect(() => {
    return subscribeCatalogSynced(() => {
      void paging.load();
      if (!currentBranchId || expandedStockProductIds.size === 0) return;
      for (const productId of expandedStockProductIds) {
        void loadVariantRows(productId, currentBranchId);
      }
    });
  }, [paging.load, currentBranchId, expandedStockProductIds, loadVariantRows]);

  const handlePickInventorySearchSuggestion = useCallback((row: Pick<InventoryProductSearchRow, 'id' | 'name'>) => {
    setPinnedStockProduct({ id: row.id, label: row.name });
    setSearchQuery('');
  }, []);

  /** Listado filtrado en servidor; acá reforzamos pin y búsqueda local. El sort va al SQL. */
  const filteredStocks = useMemo(() => {
    let rows = paging.items;
    const pinnedId = pinnedStockProduct?.id?.trim();
    if (pinnedId) {
      rows = rows.filter((row) => row.productId === pinnedId);
    }

    const effectiveQuery = effectiveProductSearchQuery(stockSearchDebounced);
    if (effectiveQuery.length > 0) {
      rows = rows.filter(
        (row) =>
          matchesSearchText(row.productName, effectiveQuery) ||
          matchesSearchText(row.productSku, effectiveQuery) ||
          matchesSearchText(row.productBarcode, effectiveQuery) ||
          matchesSearchText(row.variantLabel, effectiveQuery) ||
          matchesSearchText(row.variantSku, effectiveQuery) ||
          matchesSearchText(row.variantBarcode, effectiveQuery),
      );
    }

    // #801: Interleave variant rows for expanded products.
    if (expandedStockProductIds.size > 0) {
      const interleaved: StockRow[] = [];
      for (const row of rows) {
        interleaved.push(row);
        if (expandedStockProductIds.has(row.productId)) {
          const variants = variantRowsByProduct.get(row.productId);
          if (variants && variants.length > 0) {
            interleaved.push(...variants);
          }
        }
      }
      rows = interleaved;
    }

    return rows;
  }, [stockSearchDebounced, paging.items, pinnedStockProduct?.id, expandedStockProductIds, variantRowsByProduct]);

  return {
    stocks: paging.items,
    stockPageIndex: paging.pageIndex,
    stockPageSize: paging.pageSize,
    setStockPageSize: paging.setPageSize,
    stockHasMore: paging.hasMore,
    stockLoading: paging.loading,
    /** Error del último fetch — distinto de "lista vacía" (error ≠ empty). */
    stockError: paging.error,
    stockResultTotalCount: paging.totalCount,
    searchQuery,
    setSearchQuery,
    onInventorySearchFieldChange,
    stockListSearchQuery,
    stockCategoryFilter,
    applyStockCategoryFilter,
    stockBrandFilter,
    stockSupplierFilter,
    applyStockBrandFilter,
    applyStockSupplierFilter,
    pinnedStockProduct,
    clearPinnedStockSearch,
    handlePickInventorySearchSuggestion,
    stockFilter,
    applyStockQuickViewFilter,
    stockSort,
    applyStockSort,
    loadStocks: paging.load,
    loadStockNextPage: paging.loadNextPage,
    loadStockPreviousPage: paging.loadPreviousPage,
    loadStockJumpToPage: paging.goToPage,
    filteredStocks,
    inventoryListRef: paging.flatListRef,
    inventoryWideScrollRef: paging.scrollViewRef,
    // #801: Expand/collapse variant rows
    expandedStockProductIds,
    toggleStockProductExpanded,
    variantRowsByProduct,
    variantRowsErrorByProduct,
    retryVariantRows,
  };
};
